<template>
  <div class="breadcrumb-wrapper">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/' }">
        {{ labels['/'] }}
      </el-breadcrumb-item>

      <template v-for="(item, index) in breadcrumbItems" :key="index">
        <el-breadcrumb-item
          :to="index < breadcrumbItems.length - 1 ? { path: item.path } : null"
          :class="{ 'is-current': index === breadcrumbItems.length - 1 }"
        >
          {{ item.label }}
        </el-breadcrumb-item>
      </template>
    </el-breadcrumb>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

// Route label map (Korean labels for all routes)
const labels = {
  '/': '홈',
  '/design': '디자인 관리',
  '/sample-requests': '샘플요청',
  '/quotations': '견적',
  '/production-dashboard': '작업현황',
  '/main-requests': '메인오더',
  '/inspections': '검품 관리',
  '/deliveries': '납품/반품',
  '/material-dashboard': '자재현황',
  '/material-orders': '발주서',
  '/mobile-receiving': '모바일 입고',
  '/mobile-packing': '모바일 패킹',
  '/packing-lists': '패킹리스트',
  '/receivings': '입고 목록',
  '/material-transfers': '자재전달',
  '/inbounds': '입고관리',
  '/outbounds': '출고관리',
  '/test-reports': '시험성적서',
  '/lcs': 'LC 관리',
  '/shipments': '선적 스케줄',
  '/lgs': 'LG 관리',
  '/import-invoices': '수입송장',
  '/freight-costs': '운임비용',
  '/tt-payments': 'TT 송금',
  '/debit-notes': '데빗노트',
  '/daily-labors': '일용직',
  '/material-lots': 'LOT 자재',
  '/cost-categories': '원가 카테고리',
  '/cost-comparison': '원가 비교',
  '/executive-dashboard': '경영 대시보드',
  '/approval/inbox': '결재함',
  '/expenses': '지출결의서',
  '/expenses_manage': '일별지출통계',
  '/leave/request': '휴가 신청',
  '/leave/approval': '휴가 승인',
  '/leave/calender': '휴가달력',
  '/leave/admin': '휴가관리',
  '/customer': '고객사/브랜드',
  '/brand-assignments': '브랜드 담당자',
  '/supplier': '매입처',
  '/notices': '공지사항',
  '/audit-logs': '감사 추적',
  '/profile': '프로필',
  '/work-instructions': '메인오더 상세',
};

// Route to parent group mapping
const routeGroupMap = {
  '/design': '디자인', '/sample-requests': '디자인', '/quotations': '디자인',
  '/production-dashboard': '생산', '/main-requests': '생산', '/inspections': '생산', '/deliveries': '생산',
  '/material-dashboard': '자재', '/material-orders': '자재', '/mobile-receiving': '자재', '/mobile-packing': '자재',
  '/packing-lists': '자재', '/receivings': '자재', '/material-transfers': '자재',
  '/inbounds': '자재', '/outbounds': '자재', '/test-reports': '자재',
  '/lcs': '무역', '/shipments': '무역', '/lgs': '무역', '/import-invoices': '무역',
  '/freight-costs': '무역', '/tt-payments': '무역', '/debit-notes': '무역', '/daily-labors': '무역',
  '/material-lots': '원가', '/cost-categories': '원가', '/cost-comparison': '원가', '/executive-dashboard': '원가',
  '/expenses': '지출결의서', '/expenses_manage': '지출결의서',
  '/leave/request': '휴가', '/leave/approval': '휴가', '/leave/calender': '휴가', '/leave/admin': '휴가',
  '/customer': '관리', '/brand-assignments': '관리', '/supplier': '관리', '/notices': '관리',
  '/approval/inbox': '결재',
  '/work-instructions': '생산',
};

// Compute breadcrumb items
const breadcrumbItems = computed(() => {
  if (route.meta?.breadcrumb && Array.isArray(route.meta.breadcrumb)) {
    return route.meta.breadcrumb;
  }

  const breadcrumbs = [];
  const fullPath = route.path;

  // Find base path (strip trailing ID segments like /123)
  const basePath = fullPath.replace(/\/\d+.*$/, '');
  const isDetailPage = basePath !== fullPath;

  // Add parent group
  const groupLabel = routeGroupMap[basePath];
  if (groupLabel) {
    breadcrumbs.push({ label: groupLabel, path: '' });
  }

  // Add page label
  const pageLabel = labels[basePath];
  if (pageLabel) {
    breadcrumbs.push({
      label: pageLabel,
      path: isDetailPage ? basePath : '',
    });
  }

  // Add "상세" for detail pages
  if (isDetailPage) {
    breadcrumbs.push({ label: '상세', path: '' });
  }

  return breadcrumbs;
});

</script>

<style scoped>
.breadcrumb-wrapper {
  background-color: #f5f5f5;
  padding: 8px 20px;
  border-bottom: 1px solid #e8e8e8;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  z-index: 999;
}

:deep(.el-breadcrumb) {
  font-size: 13px;
  line-height: 20px;
}

:deep(.el-breadcrumb__item) {
  display: inline-flex;
  align-items: center;
}

:deep(.el-breadcrumb__item.is-current) {
  color: #606266;
  font-weight: 500;
  pointer-events: none;
}

:deep(.el-breadcrumb__inner) {
  color: #303133;
  text-decoration: none;
  transition: color 0.2s;
}

:deep(.el-breadcrumb__inner:hover) {
  color: #409eff;
}

:deep(.el-breadcrumb__item.is-current .el-breadcrumb__inner) {
  color: #606266;
}

:deep(.el-breadcrumb__item.is-current .el-breadcrumb__inner:hover) {
  color: #606266;
  cursor: default;
}

:deep(.el-breadcrumb__separator) {
  margin: 0 6px;
  color: #c0c4cc;
}

/* Responsive */
@media (max-width: 768px) {
  .breadcrumb-wrapper {
    padding: 6px 12px;
  }

  :deep(.el-breadcrumb) {
    font-size: 12px;
  }

  :deep(.el-breadcrumb__separator) {
    margin: 0 4px;
  }
}
</style>
